import { Link } from "react-router-dom";
import { ArrowRight, Coffee, Cake } from "lucide-react";

const ingredients = [
    { dot: "bg-arl-rose", label: "Lait & yaourts bio", detail: "Ferme du Mont, Saint-Denis-lès-Bourg" },
    { dot: "bg-arl-orange", label: "Vanille de Madagascar", detail: "Gousses entières, infusion lente" },
    { dot: "bg-arl-blue", label: "Chocolat noir 65 %", detail: "Origine sélectionnée" },
    { dot: "bg-arl-mint", label: "Menthe & basilic", detail: "Du jardin de Servas" },
];

export default function Home() {
    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section
                className="relative overflow-hidden pt-10 pb-20 sm:pt-16 sm:pb-28"
                data-testid="home-hero"
            >
                <div className="absolute inset-0 rhombus-bg opacity-70 pointer-events-none" />

                <div className="relative mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                    <div className="animate-fade-up">
                        <h1
                            className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-arl-ink"
                            data-testid="hero-title"
                        >
                            La glace,
                            <br />
                            <span className="font-script text-arl-blue text-6xl sm:text-7xl lg:text-[5.5rem] inline-block leading-none">
                                vraie de vraie.
                            </span>
                        </h1>
                        <p
                            className="mt-7 text-lg text-arl-ink/75 max-w-xl leading-relaxed"
                            data-testid="hero-subtitle"
                        >
                            Crèmes glacées, sorbets plein fruit, entremets glacés, vacherins, omelettes
                            norvégiennes et bâtonnets : tout est fait maison et sur place, au goût et à la texture.
                        </p>
                        <div className="mt-9 flex flex-wrap gap-3" data-testid="hero-ctas">
                            <Link to="/nos-parfums" className="btn-primary" data-testid="hero-cta-parfums">
                                Découvrir nos parfums <ArrowRight size={18} />
                            </Link>
                            <Link to="/nous-retrouver" className="btn-ghost" data-testid="hero-cta-visit">
                                Venir à la boutique
                            </Link>
                        </div>
                    </div>

                    {/* Hero visual : photo gourmande réelle */}
                    <div className="relative">
                        <div className="relative aspect-[4/5] sm:aspect-[5/6] rounded-[2rem] overflow-hidden shadow-[0_30px_80px_-30px_rgba(36,85,99,0.45)] border border-arl-blue/10">
                            <img
                                src="/images/hero.jpg"
                                alt="Cornet de glace devant la boutique Arlequin"
                                className="absolute inset-0 w-full h-full object-cover"
                                data-testid="hero-image"
                            />
                        </div>

                        <div className="hidden sm:flex absolute -top-6 -left-6 w-28 h-28 rounded-full bg-arl-sun shadow-xl items-center justify-center text-center font-script text-arl-blue text-2xl leading-tight rotate-[-8deg] animate-float-slow">
                            Tout
                            <br />
                            fait maison
                        </div>
                    </div>
                </div>
            </section>

            {/* MANGER SUR PLACE */}
            <section
                className="relative py-20 sm:py-24 bg-arl-paper"
                data-testid="home-sur-place"
            >
                <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                    <div className="relative order-2 lg:order-1">
                        <div className="absolute -inset-4 sm:-inset-6 bg-arl-rose/10 rounded-[2.2rem] rotate-[-2deg]" />
                        <img
                            src="/images/interieur.png"
                            alt="L'intérieur de la boutique Arlequin avec ses tables, cookies et gaufres"
                            className="relative w-full aspect-[4/3] object-cover rounded-[1.8rem] shadow-xl border border-white"
                            data-testid="interieur-image"
                        />
                    </div>

                    <div className="order-1 lg:order-2">
                        <h2 className="font-display text-4xl sm:text-5xl leading-tight text-arl-ink">
                            Posez-vous,
                            <span className="font-script text-arl-rose"> on s'occupe du reste.</span>
                        </h2>
                        <p className="mt-5 text-lg text-arl-ink/75 leading-relaxed">
                            On peut aussi manger sur place. Installez-vous autour d'une table, on vous sert un café, une boule (ou trois), un cookie tout juste sorti ou une gaufre dorée. Le bon prétexte pour un goûter qui prend son temps.
                        </p>

                        <div className="mt-8 grid sm:grid-cols-2 gap-4" data-testid="sur-place-features">
                            <div className="rounded-2xl bg-white p-5 border border-arl-blue/10 hover:border-arl-blue/30 transition shadow-sm">
                                <div className="w-10 h-10 rounded-xl bg-arl-blue/10 text-arl-blue flex items-center justify-center mb-3">
                                    <Coffee size={20} />
                                </div>
                                <h3 className="font-semibold text-arl-ink">Café & boissons</h3>
                                <p className="text-sm text-arl-ink/65 mt-1">
                                    Pour accompagner une douceur ou faire une pause.
                                </p>
                            </div>
                            <div className="rounded-2xl bg-white p-5 border border-arl-blue/10 hover:border-arl-blue/30 transition shadow-sm">
                                <div className="w-10 h-10 rounded-xl bg-arl-coral/20 text-arl-orange flex items-center justify-center mb-3">
                                    <Cake size={20} />
                                </div>
                                <h3 className="font-semibold text-arl-ink">Goûters maison</h3>
                                <p className="text-sm text-arl-ink/65 mt-1">
                                    Cookies, gaufres et pâtisseries préparés sur place.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* L'ARTISAN — refonte éditoriale */}
            <section className="relative py-20 sm:py-28" data-testid="home-artisan">
                <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
                    <div className="lg:col-span-7 order-2 lg:order-1">
                        <h2
                            className="font-display text-4xl sm:text-5xl leading-tight text-arl-ink"
                            data-testid="artisan-title"
                        >
                            Thomas Névoret,
                            <span className="font-script text-arl-blue"> glacier passionné.</span>
                        </h2>

                        <div className="mt-7 space-y-5 text-lg text-arl-ink/80 leading-relaxed">
                            <p>
                                Vingt ans en boulangerie-pâtisserie à Lyon, un CAP Glacier obtenu à
                                Saint-Étienne en apprentissage chez Terre adélice, et l'envie de créer à
                                son rythme.
                            </p>
                            <p>
                                Des recettes signées, des matières premières choisies avec exigence, et
                                une seule règle : ne tricher sur rien.
                            </p>
                        </div>

                        <figure
                            className="mt-9 relative pl-6 sm:pl-10 py-2"
                            data-testid="artisan-quote"
                        >
                            <span
                                aria-hidden="true"
                                className="absolute left-0 top-0 font-display text-7xl leading-none text-arl-rose/40 select-none"
                            >
                                “
                            </span>
                            <blockquote className="font-display italic text-2xl sm:text-3xl leading-snug text-arl-ink/90">
                                Beaucoup de glaces industrielles contiennent trop d'air, donc moins de
                                matière. Moi, je mise sur le goût et la texture.
                            </blockquote>
                            <figcaption className="mt-4 text-sm text-arl-ink/60">
                                Thomas Névoret · Artisan glacier
                            </figcaption>
                        </figure>

                        <div className="mt-12 pt-8 border-t border-arl-blue/15">
                            <p className="text-xs uppercase tracking-[0.22em] text-arl-blue/70 font-semibold mb-5">
                                Côté ingrédients
                            </p>
                            <ul
                                className="grid sm:grid-cols-2 gap-x-8 gap-y-4"
                                data-testid="artisan-ingredients"
                            >
                                {ingredients.map((it) => (
                                    <li key={it.label} className="flex items-start gap-3">
                                        <span className={`mt-2 w-2.5 h-2.5 rounded-full ${it.dot} shrink-0`} />
                                        <div>
                                            <p className="font-semibold text-arl-ink">{it.label}</p>
                                            <p className="text-sm text-arl-ink/60">{it.detail}</p>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    <div className="lg:col-span-5 order-1 lg:order-2">
                        <div className="relative">
                            <img
                                src="/images/miam.webp"
                                alt="Sorbet mangue, glace menthe et cookie maison chez Arlequin"
                                className="w-full aspect-[4/5] object-cover rounded-[1.8rem] shadow-xl"
                                data-testid="artisan-image"
                            />
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
